import {
  createFileRoute,
  useLoaderData,
  useNavigate,
} from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { requireAuthBeforeLoad } from '@/helpers/requireAuthBeforeLoad';
import { createOrganizer } from '@/server/organizer';
import { Navbar } from '@/components/ui/navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { safeRedirect } from '@/utils/safeRedirect';

export const Route = createFileRoute('/create-organizer')({
  validateSearch: z.object({
    redirect: z.string().optional(),
  }),
  beforeLoad: ({ context, location }) => {
    const profile = requireAuthBeforeLoad({ context, location });
    return { isSuperAdmin: profile.level === 'superadmin' };
  },
  loader: ({ context }) => ({ isSuperAdmin: context.isSuperAdmin }),
  component: CreateOrganizerPage,
});

/**
 * Page for creating a new organizer/organization.
 * Users are redirected here if they attempt to create an event
 * without being associated with an organizer.
 */
function CreateOrganizerPage() {
  const navigate = useNavigate();
  const { redirect: redirectTo } = Route.useSearch();
  const { isSuperAdmin } = useLoaderData({ from: '/create-organizer' });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [website, setWebsite] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [markUnclaimed, setMarkUnclaimed] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = name.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await createOrganizer({
        data: {
          name: name.trim(),
          description: description.trim() || undefined,
          website: website.trim() || undefined,
          contactEmail: contactEmail.trim() || undefined,
          ...(isSuperAdmin && markUnclaimed ? { markUnclaimed: true } : {}),
        },
      });

      if (redirectTo) {
        navigate({ to: safeRedirect(redirectTo, '/') });
      } else {
        navigate({
          to: '/admin/dashboard/organizer/$organizerId',
          params: { organizerId: result.organizer.id },
        });
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to create organizer'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100">
      <Navbar />

      <main className="container mx-auto px-4 py-8 md:py-12 max-w-lg">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">
          Create Your Organization
        </h1>
        <p className="text-slate-600 mb-8">
          Set up your organization to start creating and managing
          events.
        </p>

        <Card className="border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">
              Organization Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-4"
            >
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="name">
                  Organization Name{' '}
                  <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Acme Cycling Club"
                  maxLength={200}
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="description">Description</Label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Tell us about your organization"
                  maxLength={2000}
                  rows={3}
                  className="flex w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-transparent disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://example.com"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="contactEmail">Contact Email</Label>
                <Input
                  id="contactEmail"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="contact@example.com"
                />
              </div>

              {isSuperAdmin && (
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={markUnclaimed}
                    onChange={(e) => setMarkUnclaimed(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-slate-700">
                      Mark as unclaimed
                    </span>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Creates the organization without assigning you as an
                      owner, allowing organizers to claim it later.
                    </p>
                  </div>
                </label>
              )}

              {error && (
                <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                disabled={!canSubmit || isSubmitting}
                className="mt-2"
              >
                {isSubmitting && (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}
                Create Organization
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
